import type { Cart } from './types'
import { withCookieFirstRequest } from '../shared/api/cookieAuthRequest'
import { requestApiJson } from '../shared/api/client'

const toErrorMessage = (status: number, payload: unknown) => {
  if (payload && typeof payload === 'object') {
    const message = (payload as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) {
      return message.trim()
    }
  }

  if (status === 404) return 'Carrello non trovato'
  if (status === 409) return 'Operazione carrello non valida'
  if (status === 503) return 'Servizio carrello non raggiungibile'
  if (status === 504) return 'Timeout servizio carrello'
  return 'Errore gestione carrello'
}

export class CartApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

const requestJson = <T>(path: string, init?: RequestInit): Promise<T> =>
  requestApiJson<T>({
    path,
    init,
    toErrorMessage,
    createError: (status, message) => new CartApiError(status, message),
  })

export const createCart = async (cartId?: string, token?: string): Promise<Cart> => {
  const payload = await requestJson<{ cart: Cart }>('/cart', {
    ...withCookieFirstRequest({
      init: {
        method: 'POST',
        body: JSON.stringify(cartId ? { cartId } : {}),
      },
      bearerFallbackToken: token,
    }),
  })
  return payload.cart
}

export const resolveCart = async ({
  cartId,
  token,
}: {
  cartId?: string
  token?: string
} = {}): Promise<Cart> => {
  const body: Record<string, unknown> = {}
  if (cartId && cartId.trim()) body.cartId = cartId.trim()

  const payload = await requestJson<{ cart: Cart }>('/cart/resolve', {
    ...withCookieFirstRequest({
      init: {
        method: 'POST',
        body: JSON.stringify(body),
      },
      bearerFallbackToken: token,
    }),
  })
  return payload.cart
}

export const fetchCart = async (cartId: string): Promise<Cart> => {
  const payload = await requestJson<{ cart: Cart }>(`/cart/${encodeURIComponent(cartId)}`, {
    credentials: 'include',
  })
  return payload.cart
}

export const addCartItem = async (cartId: string, productId: string, quantity: number): Promise<Cart> => {
  const payload = await requestJson<{ cart: Cart }>(`/cart/${encodeURIComponent(cartId)}/items`, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ productId, quantity }),
  })
  return payload.cart
}

export const updateCartItem = async (cartId: string, itemId: string, quantity: number): Promise<Cart> => {
  const payload = await requestJson<{ cart: Cart }>(
    `/cart/${encodeURIComponent(cartId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: JSON.stringify({ quantity }),
    },
  )
  return payload.cart
}

export const removeCartItem = async (cartId: string, itemId: string): Promise<Cart> => {
  const payload = await requestJson<{ cart: Cart }>(
    `/cart/${encodeURIComponent(cartId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: 'DELETE',
      credentials: 'include',
    },
  )
  return payload.cart
}

export const clearCart = async (cartId: string): Promise<void> => {
  await requestJson<unknown>(`/cart/${encodeURIComponent(cartId)}`, {
    method: 'DELETE',
    credentials: 'include',
  })
}
